import * as Y from 'yjs'
import { Awareness, applyAwarenessUpdate, encodeAwarenessUpdate, removeAwarenessStates } from 'y-protocols/awareness'
import { io, Socket } from 'socket.io-client'

/** Версия протокола совместного редактирования (эпоха документа, один инициализатор) */
export const COLLAB_PROTOCOL = 2

export interface SocketIOProviderOptions {
  awareness?: Awareness
  autoConnect?: boolean
  /** Эпоха документа, известная клиенту (из локальной копии) */
  epoch?: number | null
}

/** Ответ сервера при входе в комнату (начальная синхронизация) */
export interface JoinedInfo {
  epoch: number
  /** Этот клиент наполняет пустой документ содержимым `content` */
  init: boolean
  /** Документ пуст, его наполняет другой клиент — показать `content` только для чтения */
  waiting: boolean
  /** Текст поля из базы (только для пустого документа) */
  content?: string
}

/** Сброс документа: поле изменили мимо редактора или клиент отстал */
export interface InvalidateInfo {
  reason: string
  epoch?: number
  /** Переинициализироваться даже при той же эпохе */
  force?: boolean
}

export class SocketIOProvider {
  doc: Y.Doc
  awareness: Awareness
  room: string
  socket: Socket
  /** Эпоха документа, с которым синхронизирован клиент; null — ещё не вошёл */
  epoch: number | null = null
  private _requestedEpoch: number | null
  private _synced = false
  private _connected = false
  private _connecting = false
  private _destroyed = false
  private _listeners: Map<string, Set<(...args: unknown[]) => void>> = new Map()
  private readonly _onDocUpdate: (update: Uint8Array, origin: unknown) => void
  private readonly _onAwarenessUpdate: (changes: { added: number[]; updated: number[]; removed: number[] }) => void
  private readonly _onBeforeUnload: () => void

  constructor(
    serverUrl: string,
    room: string,
    doc: Y.Doc,
    options: SocketIOProviderOptions = {}
  ) {
    this.doc = doc
    this.room = room
    this.awareness = options.awareness || new Awareness(doc)
    this._requestedEpoch = options.epoch ?? null

    // Create Socket.IO connection to collaboration namespace
    this.socket = io(`${serverUrl}/collaboration`, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      autoConnect: options.autoConnect !== false,
    })

    this._onDocUpdate = (update: Uint8Array, origin: unknown) => {
      if (origin === 'server') return // Don't echo server updates
      // До входа в комнату (и с неизвестной эпохой) не отправляем: сервер
      // отверг бы update, а состояние уйдёт шагом синхронизации после входа
      if (!this._synced || this.epoch === null) return
      this.socket.emit('update', {
        room: this.room,
        update: Array.from(update),
        epoch: this.epoch,
      }, (response: { success?: boolean; error?: string } | undefined) => {
        // Подтверждение сервера (по порядку отправки): нужно, чтобы снять
        // пометку «наполнение не подтверждено»
        this._emit('update-ack', response)
      })
    }
    this._onAwarenessUpdate = ({ added, updated, removed }) => {
      const changedClients = [...added, ...updated, ...removed]
      const update = encodeAwarenessUpdate(this.awareness, changedClients)
      this.socket.emit('awareness', {
        room: this.room,
        update: Array.from(update),
      })
    }
    this._onBeforeUnload = () => {
      removeAwarenessStates(this.awareness, [this.doc.clientID], 'window unload')
    }

    this._setupSocketHandlers()
    this.doc.on('update', this._onDocUpdate)
    this.awareness.on('update', this._onAwarenessUpdate)
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this._onBeforeUnload)
    }

    if (options.autoConnect !== false) {
      this.connect()
    }
  }

  get synced(): boolean {
    return this._synced
  }

  get connected(): boolean {
    return this._connected
  }

  private _setupSocketHandlers(): void {
    this.socket.on('connect', () => {
      this._connected = true
      this._connecting = false
      this._emit('status', { status: 'connected' })

      // Вход в комнату: протокол и известная эпоха
      this.socket.emit(
        'join',
        { room: this.room, epoch: this.epoch ?? this._requestedEpoch, v: COLLAB_PROTOCOL },
        (response: { success: boolean; error?: string }) => {
          if (!response?.success) {
            console.warn('[SocketIOProvider] Join rejected:', response?.error)
            this._emit('join-error', response?.error)
          }
        },
      )
    })

    this.socket.on('disconnect', () => {
      this._connected = false
      this._synced = false
      this._emit('status', { status: 'disconnected' })
      this._emit('sync', false)
    })

    this.socket.on('connect_error', (error: Error) => {
      console.error('[SocketIOProvider] Connection error:', error)
      this._emit('connection-error', error)
    })

    // Начальная синхронизация и обновления сервера
    this.socket.on('sync', (data: {
      room: string
      update: number[]
      stateVector?: number[]
      isInitial?: boolean
      epoch?: number
      init?: boolean
      waiting?: boolean
      content?: string
    }) => {
      if (data.room !== this.room) return

      if (data.isInitial) {
        this._handleInitialSync(data)
        return
      }
      // Обновление чужой эпохи — документ уже сброшен (или сбросится) — не применять
      if (typeof data.epoch === 'number' && data.epoch !== this.epoch) return
      this._applyServerUpdate(data.update)
    })

    // Handle incremental updates
    this.socket.on('update', (data: { room: string; update: number[]; epoch?: number }) => {
      if (data.room !== this.room) return
      if (typeof data.epoch === 'number' && data.epoch !== this.epoch) return
      this._applyServerUpdate(data.update)
    })

    // Handle awareness updates
    this.socket.on('awareness', (data: { room: string; update: number[] }) => {
      if (data.room !== this.room) return

      try {
        const update = new Uint8Array(data.update)
        applyAwarenessUpdate(this.awareness, update, 'server')
      } catch (error) {
        console.error('[SocketIOProvider] Failed to apply awareness update:', error)
      }
    })

    // Сброс документа (поле изменили мимо редактора, клиент отстал)
    this.socket.on('invalidate', (data: { room: string; reason: string; epoch?: number; force?: boolean }) => {
      if (data.room !== this.room) return
      // Сброс, который этот клиент уже прошёл (эпоха не новее), пропускаем
      if (!data.force && typeof data.epoch === 'number' && this.epoch !== null && data.epoch <= this.epoch) {
        return
      }
      console.warn('[SocketIOProvider] Document invalidated:', data.reason, data.epoch)
      this._emit('invalidate', { reason: data.reason, epoch: data.epoch, force: data.force } satisfies InvalidateInfo)
    })

    // Роль инициализатора перешла к этому клиенту (прежний ушёл, не наполнив документ)
    this.socket.on('crdt:init', (data: { room: string; epoch: number; content: string }) => {
      if (data.room !== this.room || data.epoch !== this.epoch) return
      this._emit('init-lease', { content: data.content })
    })
  }

  private _handleInitialSync(data: {
    update: number[]
    stateVector?: number[]
    epoch?: number
    init?: boolean
    waiting?: boolean
    content?: string
  }): void {
    const epoch = typeof data.epoch === 'number' ? data.epoch : null
    if (epoch === null) {
      console.error('[SocketIOProvider] Initial sync without epoch — server is outdated')
      return
    }
    // Переподключение, а документ за это время сбросили: локальное состояние
    // другой эпохи нельзя ни принимать, ни отправлять — только переинициализация
    if (this.epoch !== null && this.epoch !== epoch) {
      this._emit('invalidate', { reason: 'epoch_changed', epoch, force: true } satisfies InvalidateInfo)
      return
    }
    const firstJoin = this.epoch === null
    this.epoch = epoch
    this._applyServerUpdate(data.update)
    this._synced = true

    // Шаг 2 синхронизации: то, чего нет на сервере (правки офлайн)
    if (!firstJoin && Array.isArray(data.stateVector)) {
      this.pushLocalDiff(data.stateVector)
    }

    this._emit('joined', {
      epoch,
      init: !!data.init,
      waiting: !!data.waiting,
      content: data.content,
    } satisfies JoinedInfo, firstJoin)
    this._emit('sync', true)
  }

  /** Отправить серверу локальные изменения, которых у него нет */
  pushLocalDiff(serverStateVector: number[] | Uint8Array): void {
    if (!this._synced || this.epoch === null) return
    try {
      const diff = Y.encodeStateAsUpdate(this.doc, new Uint8Array(serverStateVector))
      // Пустой update — 2 байта (нет структур и удалений)
      if (diff.length <= 2) return
      this.socket.emit('update', { room: this.room, update: Array.from(diff), epoch: this.epoch })
    } catch (error) {
      console.error('[SocketIOProvider] Failed to push local diff:', error)
    }
  }

  private _applyServerUpdate(update: number[] | undefined): void {
    if (!update || !Array.isArray(update) || update.length === 0) return
    try {
      Y.applyUpdate(this.doc, new Uint8Array(update), 'server')
    } catch (error) {
      console.error('[SocketIOProvider] Failed to apply server update:', error)
    }
  }

  connect(): void {
    if (this._destroyed || this._connected || this._connecting) return
    this._connecting = true
    this.socket.connect()
  }

  disconnect(): void {
    if (!this._connected) return

    // Leave room
    this.socket.emit('leave', { room: this.room })

    // Clean up awareness
    removeAwarenessStates(this.awareness, [this.doc.clientID], 'disconnect')

    this.socket.disconnect()
    this._connected = false
    this._synced = false
  }

  destroy(): void {
    if (this._destroyed) return
    this._destroyed = true
    this.disconnect()
    // Сокет мог не успеть подключиться — закрыть в любом случае
    this.socket.removeAllListeners()
    this.socket.disconnect()
    this.doc.off('update', this._onDocUpdate)
    this.awareness.off('update', this._onAwarenessUpdate)
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this._onBeforeUnload)
    }
    this._listeners.clear()
  }

  /**
   * Finalize document - triggers immediate entity update on server
   * Call this on blur or before closing to ensure history/annotations are triggered
   */
  finalize(): void {
    if (!this._connected) return
    this.socket.emit('finalize', { room: this.room }, (response: { success: boolean }) => {
      if (response?.success) {
        console.log('[SocketIOProvider] Document finalized')
      }
    })
  }

  // Event emitter methods
  on(event: string, callback: (...args: unknown[]) => void): void {
    if (!this._listeners.has(event)) {
      this._listeners.set(event, new Set())
    }
    this._listeners.get(event)!.add(callback)
  }

  off(event: string, callback: (...args: unknown[]) => void): void {
    this._listeners.get(event)?.delete(callback)
  }

  private _emit(event: string, ...args: unknown[]): void {
    this._listeners.get(event)?.forEach(callback => {
      try {
        callback(...args)
      } catch (error) {
        console.error(`[SocketIOProvider] Error in event handler for ${event}:`, error)
      }
    })
  }
}
